import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { AuthenticationResponse } from "../../interfaces/authentication/AuthenticationResponse";
import { UserResponse } from "../../interfaces/authentication/UserResponse";
import { AN_ERROR_OCCURRED_MESSAGE } from "../../utils/constants";
import authenticationService from "../../services/authenticationService";

interface AuthenticationState {
  user?: AuthenticationResponse | null;
  allUsers: UserResponse[];
  error: string | null;
  loading: boolean;
}

const authenticationInitialState: AuthenticationState = {
  user: JSON.parse(
    localStorage.getItem("user") || "null"
  ) as AuthenticationResponse | null,
  allUsers: [],
  error: null,
  loading: false,
};

export const login = createAsyncThunk<
  AuthenticationResponse,
  { userName: string; password: string },
  { rejectValue: string }
>("auth/login", async ({ userName, password }, { rejectWithValue }) => {
  try {
    return await authenticationService.login(userName, password);
  } catch (error: any) {
    return rejectWithValue(
      `Неуспешен вход: ${error.message}` || "Неуспешен вход:"
    );
  }
});

export const register = createAsyncThunk<
  void,
  { userName: string; email: string; password: string },
  { rejectValue: string }
>(
  "auth/register",
  async ({ userName, email, password }, { rejectWithValue }) => {
    try {
      await authenticationService.register(userName, email, password, password);
    } catch (error: any) {
      return rejectWithValue(
        `Registration failed: ${error.message}` || "Registration failed"
      );
    }
  }
);

export const refreshToken = createAsyncThunk<
  AuthenticationResponse,
  void,
  { rejectValue: string }
>("auth/refreshToken", async (_, { rejectWithValue }) => {
  try {
    return await authenticationService.refreshToken();
  } catch (error: any) {
    return rejectWithValue(
      `Token refresh failed: ${error.message}` || "Token refresh failed"
    );
  }
});

export const fetchAllUsers = createAsyncThunk<
  UserResponse[],
  void,
  { rejectValue: string }
>("authentication/getAllUsers", async (_, { rejectWithValue }) => {
  try {
    const allUsers = await authenticationService.getAllUsers();
    return allUsers;
  } catch (error: any) {
    return rejectWithValue(error.message || AN_ERROR_OCCURRED_MESSAGE);
  }
});

const authenticationSlice = createSlice({
  name: "authentication",
  initialState: authenticationInitialState,
  reducers: {
    logout: (state) => {
      authenticationService.logout();
      state.user = null;
    },
  },
  extraReducers: (actionReducerMapBuilder) => {
    actionReducerMapBuilder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(refreshToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.allUsers = action.payload;
        state.loading = false;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export const { logout } = authenticationSlice.actions;

export default authenticationSlice.reducer;